import { z } from 'zod'

/**
 * Die Aufnahme-Checkliste für einen Server (Feature „Onboarding-Checkliste").
 *
 * Sie beantwortet eine einzige Frage: Ist dieser Server fertig eingerichtet, oder fehlt noch
 * etwas? Gemischt aus zwei Quellen, weil beide Hälften zählen — was auf der Maschine steht
 * (Firewall, SSH-Härtung, Updates) und was in Kontor hinterlegt ist (Backup, Alarm, sudo,
 * Host-Schlüssel). Ein Server mit perfekter Firewall und ohne Backup ist nicht fertig.
 */
export const checklistStatusSchema = z.enum(['ok', 'warn', 'error', 'unknown'])
export type ChecklistStatus = z.infer<typeof checklistStatusSchema>

/** Woher der Punkt kommt — die Oberfläche trennt danach, weil man sie unterschiedlich behebt. */
export const checklistAreaSchema = z.enum(['server', 'kontor'])
export type ChecklistArea = z.infer<typeof checklistAreaSchema>

export const checklistItemSchema = z.object({
  key: z.string(),
  area: checklistAreaSchema,
  title: z.string(),
  status: checklistStatusSchema,
  detail: z.string(),
  /** Warum das zählt. Nur gesetzt, wenn etwas zu tun ist. */
  hint: z.string().nullable(),
  /**
   * Ein Befehl, der es beheben würde — zum Lesen und selbst Ausführen. Kontor führt ihn
   * absichtlich nicht auf Knopfdruck aus: Ein Fehlgriff bei sshd_config sperrt dich aus deinem
   * eigenen Server aus, und das soll niemand aus Versehen anklicken.
   */
  fix: z.string().nullable(),
})
export type ChecklistItem = z.infer<typeof checklistItemSchema>

export const checklistSchema = z.object({
  serverId: z.string(),
  checkedAt: z.iso.datetime(),
  items: z.array(checklistItemSchema),
  /** Wie viele Punkte in Ordnung sind, gemessen an allen beurteilbaren. */
  done: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  /** Sammelt, was nicht gelesen werden konnte, damit die Seite nichts Leeres behauptet. */
  notes: z.array(z.string()),
})
export type Checklist = z.infer<typeof checklistSchema>
